import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ExampleBox = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const OperatorChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  margin: theme.spacing(0, 0.5),
}));

const ExampleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.5),
  display: 'inline-block',
  margin: theme.spacing(0.5, 0),
}));

function FullTextSearchInstructionsDialog({ open, onClose }: { open: boolean, onClose: () => void }) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          📖 Full Text Search Guide
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Use these operators to create powerful search queries that find exactly what you're looking for:
        </Typography>

        {/* AND Operator */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label="&" color="primary" size="small" />
            AND Search
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Find documents that contain <strong>all</strong> specified terms.
          </Typography>
          <ExampleBox>
            <ExampleText>water & sanitation</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents containing both "water" AND "sanitation"
            </Typography>
          </ExampleBox>
        </Box>

        {/* OR Operator */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label="|" color="secondary" size="small" />
            OR Search
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Find documents that contain <strong>any</strong> of the specified terms.
          </Typography>
          <ExampleBox>
            <ExampleText>Rwanda | Uganda | Tanzania</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents containing "Rwanda" OR "Uganda" OR "Tanzania"
            </Typography>
          </ExampleBox>
        </Box>

        {/* NOT Operator */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label="-" color="error" size="small" />
            NOT Search
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Exclude documents that contain specific terms.
          </Typography>
          <ExampleBox>
            <ExampleText>water -pollution</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents with "water" but NOT "pollution"
            </Typography>
          </ExampleBox>
        </Box>

        {/* Exact Phrase */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label='" "' color="info" size="small" />
            Exact Phrase
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Find the exact phrase in documents.
          </Typography>
          <ExampleBox>
            <ExampleText>"climate change adaptation"</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents containing the exact phrase "climate change adaptation"
            </Typography>
          </ExampleBox>
        </Box>

        {/* Wildcard */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label="*" color="warning" size="small" />
            Wildcard Search
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Find word variations and partial matches.
          </Typography>
          <ExampleBox>
            <ExampleText>financ*</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents with "finance", "financial", "financing", etc.
            </Typography>
          </ExampleBox>
        </Box>

        {/* Grouping */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <OperatorChip label="( )" color="success" size="small" />
            Grouping
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Group terms to control search logic with parentheses.
          </Typography>
          <ExampleBox>
            <ExampleText>(water | sanitation) & infrastructure</ExampleText>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              ➜ Returns documents with "infrastructure" AND either "water" OR "sanitation"
            </Typography>
          </ExampleBox>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Complex Examples */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          🔍 Complex Search Examples
        </Typography>

        <List dense>
          <ListItem>
            <ListItemText
              primary={
                <ExampleText>(Rwanda | Uganda | Tanzania | Kenya) & (Law | Legal | Regulatory)</ExampleText>
              }
              secondary="Find documents about legal/regulatory matters in East African countries"
            />
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary={
                <ExampleText>"project management" & (GIZ | "World Bank" | ADB) -cancelled</ExampleText>
              }
              secondary="Find project management documents from major donors, excluding cancelled projects"
            />
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary={
                <ExampleText>(water & sanitation) | (waste & management) & sustain*</ExampleText>
              }
              secondary="Find documents about water/sanitation OR waste management with sustainability focus"
            />
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary={
                <ExampleText>"climate change" & (adapt* | mitigat*) & (Africa | Asia)</ExampleText>
              }
              secondary="Find climate change adaptation or mitigation documents for Africa or Asia"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Tips */}
        <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            💡 Pro Tips
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Case insensitive" 
                secondary="Search is not case-sensitive: 'WATER' = 'water' = 'Water'" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Implicit AND" 
                secondary="Multiple words without operators are treated as AND: 'water sanitation' = 'water & sanitation'" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Special characters" 
                secondary='Use quotes around terms with hyphens or special characters: "mid-term" should be "mid-term"' 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Nested grouping" 
                secondary="You can nest parentheses for complex logic: ((A | B) & C) | (D & E)" 
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          size="large"
        >
          Got it! 🚀
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FullTextSearchInstructionsDialog;